// Imports for external libraries go here.
import React, { FC, useState, useRef } from 'react';
/* eslint-disable @nx/enforce-module-boundaries */
/* eslint-disable @typescript-eslint/no-explicit-any */
// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { ConversationalAISearchBannerProps } from './ConversationalAISearchBanner.types';
import { StyledConversationalAISearchBanner } from './ConversationalAISearchBanner.styles';
import { MTag } from '@marriott/mi-web-ui-kit';
import { MTagType } from '@marriott/mi-web-ui-kit';
import { AIcon, AIconSize } from '@marriott/mi-web-ui-kit';
import { useSearchFormStore } from '../../modules/store/searchFormStore';
import { useCheckBreakpoint } from '../../hooks/lib/useCheckBreakpoint';
import clsx from 'clsx';
import { useStaticDataContext } from '../../modules/context';
import { CONVO_AI_ENTRY_COOKIE, CONVO_AI_ENTRY_COOKIE_MAX_AGE } from '../../constants/lib/constants';
import { getSearchFormClickTrackingLoc } from '../../modules/utils/searchFormHelper';

/**
 * ConversationalAISearchBanner - Entry point component for AI-powered search functionality
 *
 * This banner component serves as a call-to-action to drive users to the conversational AI search experience.
 * It adapts its layout and messaging based on viewport size and provides hover states for better UX.
 * The component opens the AI search in a new tab/window to preserve the current user session.
 */
export const ConversationalAISearchBanner: FC<ConversationalAISearchBannerProps> = props => {
  const { isNextGenSearchForm, isBaselineSearchForm } = props;
  // Controls visibility based on secondary search form state - css changes when secondary form is shown
  const showSecondarySearchForm = useSearchFormStore((state: any) => state.showSecondarySearchForm);

  // Track if any dropdown is currently open - used to prevent navigation when user clicks to close dropdown
  const activePopup = useSearchFormStore((state: any) => state.activePopup);
  const setActivePopup = useSearchFormStore((state: any) => state.setActivePopup);

  // Determines responsive layout - desktop shows horizontal layout with extended messaging
  const isDesktopView = useCheckBreakpoint('viewportL');

  // Static content from CMS/configuration - allows for A/B testing and localization
  const { convoAISearchTitle, convoAIBetaTag, convoAISearchMessageDesktop, convoAISearchCTA } = useStaticDataContext();
  // Ref to track if we should block navigation (set in mousedown/touchstart, checked in click)
  const shouldBlockNavigationRef = useRef(false);
  // Ref to track if touch event already handled this interaction (prevents synthetic mousedown from resetting)
  const touchHandledRef = useRef(false);

  const handleTouchStart = () => {
    // Mark that touch handled this interaction
    touchHandledRef.current = true;
    // Reset block ref at start to avoid stale state from previous interactions
    shouldBlockNavigationRef.current = false;

    // Only block navigation on desktop viewport - on small mobile, users close dropdowns via X button
    if (isDesktopView && activePopup) {
      shouldBlockNavigationRef.current = true;
      setActivePopup(null);
    }
  };

  const handleMouseDown = () => {
    // Skip if touch already handled this interaction (prevents synthetic mousedown from resetting the ref)
    if (touchHandledRef.current) {
      touchHandledRef.current = false;
      return;
    }

    // Reset block ref at start to avoid stale state from previous interactions
    shouldBlockNavigationRef.current = false;

    // Only block navigation on desktop viewport - on small mobile, users close dropdowns via X button
    if (isDesktopView && activePopup) {
      shouldBlockNavigationRef.current = true;
      setActivePopup(null);
    }
  };

  // Determine if hover effects should be disabled (when dropdown is open on desktop)
  const disableHover = isDesktopView && !!activePopup;

  // Hover state for interactive arrow icon transition
  const [isHovered, setIsHovered] = useState(false);

  return (
    <StyledConversationalAISearchBanner
      data-testid="shop-ConversationalAISearchBanner"
      data-component-name="m-shop-ConversationalAISearchBanner"
      $showSecondarySearchForm={showSecondarySearchForm}
      $disableHover={disableHover || touchHandledRef.current}
      className={clsx('px-0', props.isNextGenHp ? 'pt-lg-2 pb-lg-3 pt-2' : 'py-2')}
    >
      {/* 
        Entire banner is wrapped in an anchor tag to make the full area clickable
        Opens in new tab to preserve user's current search session context
        Analytics tracking included for conversion measurement
      */}
      <a
        {...{
          custom_click_track_value: `${getSearchFormClickTrackingLoc(
            isNextGenSearchForm,
            isBaselineSearchForm
          )}|Search with AI|internal|event206`,
        }}
        href={convoAISearchCTA}
        target="_blank"
        className={clsx(
          // Responsive layout: desktop spreads content, mobile centers everything
          'justify-content-lg-between',
          'justify-content-center',
          'custom_click_track',
          props.isNextGenHp ? 'conversational-ai-search-entry-banner-nextgen' : 'conversational-ai-search-entry-banner'
        )}
        onMouseDownCapture={handleMouseDown}
        onTouchStartCapture={handleTouchStart}
        onClick={e => {
          // Reset touch handled ref for next interaction
          touchHandledRef.current = false;
          // Check the ref set in mousedown/touchstart to determine if we should block navigation
          if (shouldBlockNavigationRef.current) {
            e.preventDefault();
            shouldBlockNavigationRef.current = false;
            return;
          }
          document.cookie = `${CONVO_AI_ENTRY_COOKIE}=true; Path=/; SameSite=Strict; Max-Age=${CONVO_AI_ENTRY_COOKIE_MAX_AGE}`;
        }}
        onMouseEnter={() => {
          // Don't show hover if we're blocking navigation (dropdown was just closed)
          if (!disableHover && !shouldBlockNavigationRef.current) {
            setIsHovered(true);
          }
        }}
        onMouseLeave={() => {
          setIsHovered(false);
        }}
      >
        <div className="conversational-ai-search-entry-banner-content">
          <div className="justify-content-center d-flex align-items-center">
            <AIcon icon="icon-new icon-decorative" size={AIconSize.L} />
          </div>
          <div className="conversational-ai-search-entry-banner-content-title">{convoAISearchTitle}</div>

          {/* Conditionally render beta tag - allows for easy removal when feature graduates */}
          {convoAIBetaTag !== '' && (
            <div>
              <MTag aLabel={{ text: convoAIBetaTag }} type={MTagType.Alternate} />
            </div>
          )}

          {/* Desktop-only extended messaging - mobile relies on title and beta tag for brevity */}
          <div className="d-none d-lg-block conversational-ai-search-entry-banner-content-message">
            {convoAISearchMessageDesktop}
          </div>
        </div>
        {/* 
          Interactive arrow that changes on hover to indicate external link behavior
          Forward arrow (default) -> External arrow (hover) provides clear UX signal
        */}
        {isHovered ? (
          <div>
            <AIcon
              className="d-flex justify-content-center align-items-center"
              icon="icon-external-arrow"
              size={AIconSize.L}
            />
          </div>
        ) : (
          <div>
            <AIcon
              className="d-flex justify-content-center align-items-center"
              icon="icon-forward-arrow"
              size={AIconSize.L}
            />
          </div>
        )}
      </a>
    </StyledConversationalAISearchBanner>
  );
};
